/**
 * The post-processing stack: what turns a correct 3D render into a printed
 * illustration.
 *
 * Five effects, all individually toggleable, all tuned to be *barely* noticeable
 * on their own and unmistakable when you switch them off:
 *
 *   1. INTERIOR INK LINES — a depth + normal edge pass. Inverted-hull outlines
 *      (see `cel.ts`) can only ever draw the silhouette; they are physically
 *      incapable of drawing the seam between a machine's front panel and its
 *      side, or the crease where a sofa cushion meets its arm. Those interior
 *      lines are half of what makes the reference art read as *drawn*. We get
 *      them by rendering view-space normals + linear depth into an RGBA8 buffer
 *      (octahedral normal in RG, 16-bit depth packed into BA) and running a
 *      Roberts cross over it. Crucially these lines are THINNER and softer than
 *      the hull outlines: silhouette is a brush, interior is a pen.
 *
 *   2. SELECTIVE BLOOM — only on emissive materials. Never a global threshold
 *      bloom: with `NoToneMapping` and an HDR buffer, half the lit cream floor
 *      sits above 1.0 and a threshold bloom would fog the entire image. The
 *      selection is auto-populated from any material with a non-black emissive,
 *      or any object tagged `userData.glow = true`, so scenes get scanner-screen
 *      glow for free. If nothing glows, the whole bloom pass is skipped.
 *
 *   3. COLOUR GRADE — a gentle split-tone pulling shadows toward bottle green
 *      and highlights toward warm cream, plus a whisper of saturation and
 *      contrast. Roughly what a printer's ink set does to flat digital colour.
 *
 *   4. VIGNETTE — elliptical, aspect-corrected, and tinted toward deep green
 *      instead of black, because black corners read as "camera" and green
 *      corners read as "page".
 *
 *   5. PAPER GRAIN — a baked, tiling, *static* noise texture, locked to the
 *      WORLD rather than to the screen. Static is the whole point: animated
 *      grain reads as film or video noise, static grain reads as paper. But
 *      screen-locked grain is only static while the camera is; the moment it
 *      pans, the illustration slides under a fixed noise field and the whole
 *      frame crawls. World-locking is what "static" was always trying to mean.
 *      It is masked out of the darkest values so ink stays solid.
 *
 * Everything is authored so the default is tasteful. The house rule: if bloom is
 * visible as a haze, it is too strong.
 */
import {
  BlendFunction,
  Effect,
  EffectComposer,
  EffectPass,
  RenderPass,
  SelectiveBloomEffect,
} from 'postprocessing';
import {
  Camera,
  CanvasTexture,
  Color,
  HalfFloatType,
  LinearFilter,
  Mesh,
  NearestFilter,
  type Material,
  NoColorSpace,
  Object3D,
  OrthographicCamera,
  RGBAFormat,
  RepeatWrapping,
  Scene,
  ShaderMaterial,
  Uniform,
  UnsignedByteType,
  Vector2,
  Vector3,
  Vector4,
  WebGLRenderTarget,
  WebGLRenderer,
  DoubleSide,
} from 'three';
import { INK_OCCLUDER_LAYER, OUTLINE_LAYER, isInkLayerEnabled } from './cel';
import { srgb } from './palette';
import type { MoodId } from './lighting';

/* ------------------------------------------------------------------ *
 * Settings
 * ------------------------------------------------------------------ */

export interface BloomSettings {
  enabled: boolean;
  /** Keep low. 0.6 is already "that screen is lit". 1.5 is a sci-fi shooter. */
  intensity: number;
  /** Mip blur radius, 0..1. */
  radius: number;
  /** Mip levels. Fewer = tighter halo. */
  levels: number;
  luminanceThreshold: number;
  luminanceSmoothing: number;
}

export interface InkSettings {
  enabled: boolean;
  /** 0..1 overall opacity of interior lines. */
  strength: number;
  /**
   * Pen radius in CSS pixels. A Roberts cross draws a stroke roughly twice its
   * sampling radius, so 1.25 lands the ~2.5px interior line the reference uses.
   *
   * CSS, not device: the offsets are multiplied by the renderer's pixel ratio
   * so a line keeps the same physical width on a retina panel. Before that
   * correction the same setting drew a half-width line at dpr 2.
   */
  thicknessPx: number;
  /**
   * Crease gate, in radians of surface-normal fold.
   *
   * The metric is a two-radius band-pass on `length(n0 - n1)`, which is the turn
   * angle to first order with smooth curvature subtracted out — so the number
   * means the fold itself, not "how curved is this prop". A setting of 0.11
   * starts drawing at about 12 degrees and is at full weight by 30.
   *
   * The old metric was `1 - dot(n0, n1)`, which is quadratic in the angle near
   * zero: 0.1 silently meant "ignore anything under 26 degrees", i.e. most of a
   * drawer front, a hem or a collar. That is the main reason we measured
   * essentially no interior lines.
   */
  normalThreshold: number;
  /**
   * Surface step that counts as a seam, in WORLD units.
   *
   * The test is a slope-invariant second difference (`|d0 + d1 - 2*dc|`), not a
   * raw depth difference, so it reads zero on a flat surface at ANY angle to the
   * camera and non-zero only at a genuine step or crease. That is what lets the
   * threshold be a few millimetres instead of the quarter of a world unit the
   * raw difference needed to avoid setting the whole tilted floor on fire.
   */
  depthThreshold: number;
  /**
   * Weight for edges against empty space, relative to `strength`. These sit
   * under the inverted-hull shell on solid props (invisible), but they are the
   * only outline open geometry ever gets.
   */
  silhouette: number;
  /**
   * Multiplier on the interior line's own opacity, clamped at 1.
   *
   * A line at 0.9 leaves a tenth of a bright cream floor showing through, which
   * in sRGB is a mid grey, not ink — the pass looked "enabled but faint" for
   * exactly this reason. Slightly over-driving lets a scene tune `strength`
   * downward for taste without the core of the stroke going soft.
   */
  interiorGain: number;
  /** Multiplicative ink tint (sRGB hex). Multiply keeps lines from going flat. */
  color: number;
}

export interface GradeSettings {
  enabled: boolean;
  /** 0..1 blend of the split-tone. */
  strength: number;
  /** Linear multiplier applied to shadows. Near 1. */
  shadowTint: [number, number, number];
  /** Linear multiplier applied to highlights. Near 1. */
  highlightTint: [number, number, number];
  /** 1 = untouched. */
  saturation: number;
  /** 1 = untouched. Pivots around linear 0.18. */
  contrast: number;
}

export interface VignetteSettings {
  enabled: boolean;
  /** 0..1. Above ~0.5 it stops being subtle. */
  strength: number;
  /** Radius where darkening starts, in normalised screen radii. */
  offset: number;
  /** Falloff width. */
  feather: number;
  /** sRGB hex the corners are tinted toward. */
  color: number;
}

export interface GrainSettings {
  enabled: boolean;
  /** 0..1. 0.05 is "printed"; 0.2 is "broken TV". */
  strength: number;
  /** Device pixels per grain texel. ~1.5 keeps it fine on retina. */
  scale: number;
  /** Extra low-frequency paper-fibre modulation, 0..1. */
  fibre: number;
}

export interface PostFxSettings {
  bloom: BloomSettings;
  ink: InkSettings;
  grade: GradeSettings;
  vignette: VignetteSettings;
  grain: GrainSettings;
}

export type EffectName = keyof PostFxSettings;

function baseSettings(): PostFxSettings {
  return {
    bloom: {
      enabled: true,
      intensity: 0.62,
      radius: 0.58,
      levels: 5,
      luminanceThreshold: 0.22,
      luminanceSmoothing: 0.45,
    },
    ink: {
      enabled: true,
      strength: 1.0,
      thicknessPx: 1.2,
      normalThreshold: 0.11,
      /**
       * World units.
       *
       * ── Raised 0.004 -> 0.010 on 2026-08-06, on a USER report ────────────
       *
       * The user reported the contours as "too thick" and, on the oval rugs,
       * as an outline that "wobbles and thickens unevenly". Measured with
       * `tools/inktune.mjs`, raising this gate is what removes that wobble,
       * and the effect saturates immediately: rug crop near-black area
       * 7.34% -> 6.84% at 0.008 and 6.82% at 0.024. So the whole population of
       * edges being removed lives in a narrow 4-8mm band.
       *
       * That band is not seams. It is stacked floor decals: `rugRect` is a
       * 0.07-high slab with a 0.075-high panel inset on top, so its inner
       * boundary is a **5mm step** sitting right on the old gate — drawn where
       * the depth quantisation happened to push it over, skipped where it did
       * not. A line that exists for half its length is exactly what "wobbles
       * and thickens unevenly" describes, and it ran parallel to the shell
       * contour a few pixels inside it, which is what made the rugs read as
       * double-outlined.
       *
       * 10mm still finds every seam that is genuinely drawn in the reference —
       * drawer fronts, panel steps and worktop edges are all centimetre-scale —
       * and it stays far above the buffer's own ~0.6mm quantisation noise over
       * a 24-unit depth window.
       */
      depthThreshold: 0.01,
      silhouette: 1.0,
      /**
       * ── Lowered 1.2 -> 0.95 on 2026-08-06, on the same user report ───────
       *
       * The shader computes `line = gate * strength * interiorGain` and then
       * clamps to 1. Every scene runs `strength` at 0.9-1.0, so at gain 1.2
       * the product was 1.08-1.2 — clipped — and EVERY interior line was laid
       * down at the same 100% opaque black as the silhouette shell. The two
       * systems stopped being distinguishable, and this module's own stated
       * rule ("silhouette is a brush, interior is a pen", ~a third of the
       * weight) was not being honoured by the numbers.
       *
       * 0.95 puts Game 1 at 0.855 — still a confident pen, but off the clip, so
       * the interior linework sits under the contour instead of competing with
       * it. Measured effect on the crop that showed the defect worst
       * (`tools/inktune.mjs`, rect mat): near-black area 12.2% -> ~6%, against
       * 5.4% with the interior pass switched off entirely.
       *
       * Do not push this back over 1/strength. If a scene wants heavier
       * interior lines it should raise its own `strength`, where the clipping
       * is at least visible in the setting that caused it.
       */
      interiorGain: 0.95,
      color: 0x14120f,
    },
    grade: {
      enabled: true,
      strength: 0.5,
      shadowTint: [0.955, 1.015, 0.985],
      highlightTint: [1.035, 1.005, 0.955],
      saturation: 1.05,
      contrast: 1.035,
    },
    vignette: {
      enabled: true,
      strength: 0.3,
      offset: 0.62,
      feather: 0.66,
      color: 0x2b4a33,
    },
    grain: {
      enabled: true,
      strength: 0.05,
      scale: 1.5,
      fibre: 0.45,
    },
  };
}

/** Per-mood overrides. Deliberately small deltas — the look must stay one look. */
export const POSTFX_PRESETS: Record<MoodId, PostFxSettings> = {
  hub: (() => {
    const s = baseSettings();
    s.vignette.strength = 0.26;
    s.grade.strength = 0.45;
    // The hub's props are small and mostly silhouette, so its seams carry less
    // of the drawing than a full-bleed interior's do. Still nowhere near the old
    // 0.66, which left a bright surface a tenth un-inked and read as grey.
    s.ink.strength = 0.94;
    return s;
  })(),
  home: (() => {
    const s = baseSettings();
    s.grade.shadowTint = [0.95, 1.005, 0.99];
    s.grade.highlightTint = [1.045, 1.008, 0.94];
    s.vignette.strength = 0.34;
    s.vignette.color = 0x3b4a2c;
    s.ink.strength = 1.0;
    return s;
  })(),
  factory: (() => {
    const s = baseSettings();
    s.grade.shadowTint = [0.945, 1.005, 1.02];
    s.grade.highlightTint = [1.02, 1.01, 0.975];
    s.grade.contrast = 1.055;
    s.vignette.strength = 0.32;
    s.vignette.color = 0x173721;
    s.bloom.intensity = 0.72;
    s.ink.strength = 1.0;
    return s;
  })(),
};

/* ------------------------------------------------------------------ *
 * Shared GLSL
 * ------------------------------------------------------------------ */

/**
 * Octahedral normal encoding: a unit vector survives in two 8-bit channels with
 * about 1.5 degrees of error, which is far below any crease threshold we care
 * about, and leaves the other two channels free for a 16-bit depth.
 */
const OCT_GLSL = /* glsl */ `
vec2 ccSignNotZero( vec2 v ) {
  return vec2( v.x >= 0.0 ? 1.0 : -1.0, v.y >= 0.0 ? 1.0 : -1.0 );
}
vec2 ccOctEncode( vec3 n ) {
  n /= ( abs( n.x ) + abs( n.y ) + abs( n.z ) + 1e-6 );
  vec2 e = ( n.z >= 0.0 ) ? n.xy : ( ( 1.0 - abs( n.yx ) ) * ccSignNotZero( n.xy ) );
  return e * 0.5 + 0.5;
}
vec3 ccOctDecode( vec2 e ) {
  e = e * 2.0 - 1.0;
  vec3 n = vec3( e.x, e.y, 1.0 - abs( e.x ) - abs( e.y ) );
  float t = max( -n.z, 0.0 );
  n.xy += vec2( n.x >= 0.0 ? -t : t, n.y >= 0.0 ? -t : t );
  return normalize( n );
}
vec2 ccPack16( float v ) {
  v = clamp( v, 0.0, 0.99999 );
  float e = v * 255.0;
  float hi = floor( e );
  return vec2( hi / 255.0, e - hi );
}
float ccUnpack16( vec2 p ) {
  return p.x + p.y * ( 1.0 / 255.0 );
}
`;

/* ------------------------------------------------------------------ *
 * Normal + depth G-buffer
 * ------------------------------------------------------------------ */

/**
 * One extra scene render into an RGBA8 target holding view-space normal and
 * linear depth. This is the only thing in the pipeline that costs a real draw
 * pass, so it is skipped entirely when interior ink lines are off.
 *
 * Depth is normalised across a *window* around the camera rather than the whole
 * frustum. The visible slab of a 2.5D scene is maybe 40 world units deep while
 * the ortho frustum is hundreds; normalising over the window buys roughly a
 * hundredfold precision gain, which is the difference between finding a 2cm
 * panel seam and finding nothing.
 */
class NormalDepthBuffer {
  readonly target: WebGLRenderTarget;
  readonly material: ShaderMaterial;

  private readonly _clearColor = new Color();
  private _clearAlpha = 1;

  /**
   * Paint hidden for the duration of one geo render; restored immediately after.
   * Pre-allocated and reused — this runs inside the frame loop.
   */
  private readonly _hidden: Object3D[] = [];

  constructor() {
    this.target = new WebGLRenderTarget(1, 1, {
      type: UnsignedByteType,
      format: RGBAFormat,
      minFilter: NearestFilter,
      magFilter: NearestFilter,
      depthBuffer: true,
      stencilBuffer: false,
      generateMipmaps: false,
      samples: 0,
    });
    this.target.texture.name = 'PostFx.NormalDepth';
    // Packed data, not colour. sRGB decode here would silently corrupt it.
    this.target.texture.colorSpace = NoColorSpace;

    this.material = new ShaderMaterial({
      name: 'NormalDepthMaterial',
      // Double-sided so open geometry (planes, ribbons, flags) still writes a
      // usable normal instead of punching a hole in the buffer.
      side: DoubleSide,
      uniforms: {
        uDepthNear: new Uniform(0),
        uDepthSpan: new Uniform(100),
      },
      vertexShader: /* glsl */ `
        uniform float uDepthNear;
        uniform float uDepthSpan;
        varying vec3 vViewNormal;
        varying float vDepth01;
        void main() {
          vec4 mv = modelViewMatrix * vec4( position, 1.0 );
          vViewNormal = normalMatrix * normal;
          // Horizontal tops under a dimetric camera land closer than a
          // character standing just south of them. Push those faces back
          // so the occluder (and the figure) win; vertical sides stay put.
          vec3 worldN = normalize( mat3( modelMatrix ) * normal );
          if ( worldN.y > 0.45 ) mv.z -= 3.0;
          // 0.004 floor keeps real geometry clear of the "no geometry" marker.
          vDepth01 = clamp( ( -mv.z - uDepthNear ) / uDepthSpan, 0.004, 1.0 );
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: /* glsl */ `
        ${OCT_GLSL}
        varying vec3 vViewNormal;
        varying float vDepth01;
        void main() {
          vec3 n = normalize( vViewNormal );
          if ( !gl_FrontFacing ) n = -n;
          gl_FragColor = vec4( ccOctEncode( n ), ccPack16( vDepth01 ) );
        }
      `,
    });
  }

  setSize(w: number, h: number): void {
    this.target.setSize(Math.max(1, w), Math.max(1, h));
  }

  /**
   * Update the depth window from the camera. Allocation-free.
   *
   * Returns the span in world units so the interior-line pass can convert its
   * world-unit seam threshold into the normalised units the buffer stores.
   */
  setDepthWindow(camera: OrthographicCamera, focus: Vector3): number {
    const dist = camera.position.distanceTo(focus);
    const viewHeight = (camera.top - camera.bottom) / (camera.zoom || 1);
    const span = Math.max(viewHeight * 2.4, 24);
    this.material.uniforms.uDepthNear!.value = Math.max(0.01, dist - span * 0.5);
    this.material.uniforms.uDepthSpan!.value = span;
    return span;
  }

  /**
   * Hide every transparent mesh for the geo pass, remembering what was hidden.
   *
   * A painted decal — a contact shadow, a highlight ring, a glow card — is a
   * quad whose shape lives entirely in its alpha channel. The override material
   * has no alpha, so the quad writes its full RECTANGLE into the depth buffer,
   * and the interior-line pass faithfully draws a crisp black box around every
   * soft oval in the scene. That box is not a subtle artifact: it is the single
   * most obvious "this came out of a renderer" tell in the frame.
   *
   * The previous owner hit this on the player's shadow and worked around it by
   * moving that one quad onto `OUTLINE_LAYER`. That fixes one decal and leaves
   * every other one — the house's merged contact shadows among them — still
   * stamping. Transparency is the correct, general signal: a surface you can see
   * through has no business writing an opaque normal or depth.
   */
  private hidePaint(root: Object3D): void {
    this._hidden.length = 0;
    root.traverse(this._collect);
  }

  private readonly _collect = (o: Object3D): void => {
    if (!o.visible) return;
    // Floor decals (rugs) opt out explicitly. They are opaque paint, so the
    // `transparent` hatch below does not catch them, but any crease they write
    // here is composited over the player — the character billboard is itself
    // excluded from this buffer, so it cannot occlude those lines.
    if (o.userData.hideFromInk === true) {
      o.visible = false;
      this._hidden.push(o);
      return;
    }
    const mat = (o as Mesh).material as Material | Material[] | undefined;
    if (!mat) return;
    if (Array.isArray(mat)) {
      for (let i = 0; i < mat.length; i++) {
        if (!mat[i]?.transparent) continue;
        o.visible = false;
        this._hidden.push(o);
        return;
      }
      return;
    }
    if (!mat.transparent) return;
    o.visible = false;
    this._hidden.push(o);
  };

  private restorePaint(): void {
    for (let i = 0; i < this._hidden.length; i++) this._hidden[i]!.visible = true;
    this._hidden.length = 0;
  }

  /**
   * Render the scene's geometry into the buffer.
   *
   * The scene background is temporarily removed: three force-clears with a solid
   * background colour, which would stamp "valid geometry at depth 1" across the
   * whole buffer and give us a frame-wide false edge.
   */
  render(renderer: WebGLRenderer, scene: Scene, camera: Camera): void {
    const prevTarget = renderer.getRenderTarget();
    const prevOverride = scene.overrideMaterial;
    const prevBackground = scene.background;
    const prevLayers = camera.layers.mask;
    renderer.getClearColor(this._clearColor);
    this._clearAlpha = renderer.getClearAlpha();

    // Inverted-hull shells sit exactly on top of their fill mesh. Rendering both
    // would z-fight in this buffer, so they are excluded by layer when the
    // engine has opted in. When it hasn't, they render identically to the fill
    // and are harmless — just a few more draw calls.
    //
    // This layer mask is ALSO the opt-out the 2.5D sprites depend on, and it is
    // load-bearing: `render/sprite.ts` parks its billboards on OUTLINE_LAYER
    // precisely so they never reach this buffer. They must not, for two
    // separate reasons. Their contour is already painted into the artwork, so a
    // screen-space line on top is the "blobby, irregular" double contour
    // `docs/BRIEF.md` warns about; and the override material below has no
    // `alphaTest`, so an alpha-cut billboard would write its whole RECTANGLE
    // into the buffer and get a crisp black box drawn around it. Verified with
    // `tools/spritecheck.mjs` (billboard layerMask 2, no hull child).
    //
    // Two consequences worth stating: ink is never unconditional — anything
    // that must stay un-inked has this layer, the `transparent` flag, or
    // `userData.hideFromInk` as escape hatches — and this is another reason
    // not to broaden the mask.
    if (isInkLayerEnabled()) camera.layers.mask = prevLayers & ~(1 << OUTLINE_LAYER);

    scene.background = null;
    scene.overrideMaterial = this.material;
    this.hidePaint(scene);

    renderer.setRenderTarget(this.target);
    renderer.setClearColor(0x000000, 0);
    renderer.clear(true, true, false);
    renderer.render(scene, camera);

    this.restorePaint();
    scene.overrideMaterial = null;

    // Standing sprites are on OUTLINE_LAYER, so the override pass never saw
    // them — and must not, or the un-tested quad would stamp a black box.
    // A second pass draws their alpha-tested occluder children (layer 2)
    // into the same target: skip-marker colour, real depth. Wall creases
    // behind the figure lose the depth test; a wall he is behind keeps it.
    if (isInkLayerEnabled()) {
      const prevAutoClear = renderer.autoClear;
      renderer.autoClear = false;
      camera.layers.set(INK_OCCLUDER_LAYER);
      renderer.render(scene, camera);
      renderer.autoClear = prevAutoClear;
    }

    scene.overrideMaterial = prevOverride;
    scene.background = prevBackground;
    camera.layers.mask = prevLayers;
    renderer.setClearColor(this._clearColor, this._clearAlpha);
    renderer.setRenderTarget(prevTarget);
  }

  dispose(): void {
    this.target.dispose();
    this.material.dispose();
  }
}

/* ------------------------------------------------------------------ *
 * Effects
 * ------------------------------------------------------------------ */

/**
 * Interior ink lines.
 *
 * A Roberts cross (two diagonal pairs) plus a centre tap, rather than a full
 * Sobel: it produces a thinner, more even line for a fraction of the taps, which
 * is exactly what we want since these must stay visibly finer than the hull
 * outlines. Silhouette is a brush, interior is a pen.
 *
 * The line DARKENS the underlying colour by multiplying toward the ink tint
 * rather than replacing it. A pen stroke over paint still shows the paint; a
 * replaced pixel is a flat black scar.
 *
 * ---------------------------------------------------------------------------
 * Why this pass drew almost nothing before
 * ---------------------------------------------------------------------------
 *
 * It was enabled, it was running, and it was contributing about 1.5 percentage
 * points of ink coverage — roughly a tenth of what the reference spends on seam
 * lines. Two gates were doing the suppressing, and both were mis-scaled rather
 * than switched off, which is why the pass looked healthy from the outside.
 *
 * a) **The crease gate was quadratic.** `1 - dot(n0, n1)` is `θ²/2` for small
 *    angles, so the authored `normalThreshold` of 0.1 actually meant "ignore any
 *    crease shallower than 26 degrees". Drawer fronts, hems, collars, cushion
 *    seams and panel steps all live well under that. Measuring the turn with
 *    `length(n0 - n1)` — which IS the angle to first order — makes the same
 *    number mean ~6 degrees. The metric is also divided by the pen radius, so
 *    widening the pen no longer counts as finding more curvature.
 *
 * b) **The depth gate was a raw difference.** On a 42-degree dimetric tilt the
 *    floor's depth changes by more across two pixels than a drawer front stands
 *    proud of its carcass, so any threshold loose enough to find the drawer set
 *    the entire floor alight. The previous author's only escape was to wind the
 *    threshold up to 0.01 of the depth window — a quarter of a world unit — at
 *    which point nothing interior triggered at all and every interior line we
 *    had was coming from the normal test alone.
 *
 *    The fix is to test the second difference, `|d0 + d1 - 2*dc|`, which is zero
 *    on a flat surface at any angle and non-zero only where the surface actually
 *    bends or steps. That is slope-invariant, so the threshold can be stated in
 *    millimetres of real geometry and compared against nothing but the depth
 *    buffer's own quantisation noise.
 */
class InkLinesEffect extends Effect {
  constructor() {
    super(
      'InkLinesEffect',
      /* glsl */ `
      ${OCT_GLSL}
      uniform sampler2D uGeoBuffer;
      uniform vec2  uGeoTexel;
      uniform vec3  uInkTint;
      uniform vec4  uInkParams;   // x strength, y normalThreshold, z depthThreshold (world), w texel radius
      uniform vec3  uInkEdge;     // x silhouette weight, y interior gain, z 1 / depth-window span

      // Character occluders write (0, 1, 0, 0). Clear is (0,0,0,0); real
      // geometry packs a depth of at least 0.004 into BA.
      bool ccIsInkSkip( const in vec4 s ) {
        return s.g > 0.5 && ( s.b + s.a ) < 0.002;
      }

      vec4 ccInkTap( const in vec2 uv, const in vec4 fallback ) {
        vec4 s = texture2D( uGeoBuffer, uv );
        return ccIsInkSkip( s ) ? fallback : s;
      }

      void mainImage( const in vec4 inputColor, const in vec2 uv, out vec4 outputColor ) {
        outputColor = inputColor;
        if ( uInkParams.x <= 0.0 ) return;

        vec2 o = uGeoTexel * uInkParams.w;

        vec4 sc = texture2D( uGeoBuffer, uv );
        // Do not ink the figure. Neighbour taps that land on him are treated
        // as the centre so a wall pixel beside him does not grow a crease
        // (which would read as a second outline around the child).
        if ( ccIsInkSkip( sc ) ) return;
        vec4 s0 = ccInkTap( uv + vec2( -o.x, -o.y ), sc );
        vec4 s1 = ccInkTap( uv + vec2(  o.x,  o.y ), sc );
        vec4 s2 = ccInkTap( uv + vec2(  o.x, -o.y ), sc );
        vec4 s3 = ccInkTap( uv + vec2( -o.x,  o.y ), sc );

        float dc = ccUnpack16( sc.ba );
        float d0 = ccUnpack16( s0.ba );
        float d1 = ccUnpack16( s1.ba );
        float d2 = ccUnpack16( s2.ba );
        float d3 = ccUnpack16( s3.ba );

        // A depth of 0 means "no geometry here". Fully empty neighbourhoods have
        // nothing to outline.
        float v0 = step( 0.002, d0 );
        float v1 = step( 0.002, d1 );
        float v2 = step( 0.002, d2 );
        float v3 = step( 0.002, d3 );
        float solid = v0 + v1 + v2 + v3;
        if ( solid < 0.5 ) return;

        float line;
        if ( solid > 3.5 && dc > 0.002 ) {
          // Interior: creases dominate, second-difference depth catches steps
          // and tight fillets that never turn a normal far enough to register.
          vec3 n0 = ccOctDecode( s0.rg );
          vec3 n1 = ccOctDecode( s1.rg );
          vec3 n2 = ccOctDecode( s2.rg );
          vec3 n3 = ccOctDecode( s3.rg );

          // Chord length ~= turn angle across the pen.
          float eNear = length( n0 - n1 ) + length( n2 - n3 );

          // Second ring at twice the radius, to separate a CREASE from smooth
          // CURVATURE. On any smoothly curved surface the turn is proportional
          // to the distance sampled, so the outer ring reads exactly twice the
          // inner one and the difference below cancels to zero. At a genuine
          // crease both rings read the same angle, so the difference survives.
          //
          // Without this the gate cannot tell a drawer edge from the side of the
          // character's head: a 33px-radius sphere turns its normal about as far
          // across two pixels as a 10-degree fold does, so any threshold loose
          // enough to draw the fold also floods every rounded prop with grey.
          vec3 f0 = ccOctDecode( ccInkTap( uv - o * 2.0, sc ).rg );
          vec3 f1 = ccOctDecode( ccInkTap( uv + o * 2.0, sc ).rg );
          vec3 f2 = ccOctDecode( ccInkTap( uv + vec2(  o.x, -o.y ) * 2.0, sc ).rg );
          vec3 f3 = ccOctDecode( ccInkTap( uv + vec2( -o.x,  o.y ) * 2.0, sc ).rg );
          float eFar = length( f0 - f1 ) + length( f2 - f3 );

          float nEdge = max( eNear - 0.5 * eFar, 0.0 );

          // Slope-invariant curvature. Flat surface, any angle -> 0.
          float dCurv = abs( d0 + d1 - 2.0 * dc ) + abs( d2 + d3 - 2.0 * dc );
          // Threshold arrives in world units; uInkEdge.z carries 1/span.
          float dThresh = max( uInkParams.z * uInkEdge.z, 1e-6 );

          float nLine = smoothstep( uInkParams.y, uInkParams.y * 2.4, nEdge );
          float dLine = smoothstep( dThresh, dThresh * 3.0, dCurv );
          line = max( nLine, dLine ) * uInkParams.x * uInkEdge.y;
        } else {
          // Straddling a silhouette. Normals against empty space are meaningless,
          // so this is a pure coverage edge.
          //
          // On hull-outlined props this line lands *underneath* the inverted-hull
          // shell, which is several times wider, so it is invisible there. Where
          // it earns its keep is open, single-sided geometry — planes, cards,
          // ribbons — which an inverted hull physically cannot outline, because
          // every vertex shares one normal and there is nothing to extrude apart.
          //
          // Weighted by how centred the pixel is on the coverage boundary, NOT
          // painted flat. The flat version classified on the 4-sample count
          // alone, and a sub-pixel camera drift flips that count by one, which
          // snapped pixels between "no ink" and "full ink" — measured at ~4,700
          // px toggling per 0.7 px of drift, reading as shimmer crawling along
          // every wall top and corner. Counting the far ring as well gives the
          // count nine levels, so the same drift moves a rim pixel a quarter
          // step, and the band gains the soft rim of an anti-aliased stroke.
          // The core of the line still reaches the full authored weight.
          float u0 = step( 0.002, ccUnpack16( ccInkTap( uv - o * 2.0, sc ).ba ) );
          float u1 = step( 0.002, ccUnpack16( ccInkTap( uv + o * 2.0, sc ).ba ) );
          float u2 = step( 0.002, ccUnpack16( ccInkTap( uv + vec2(  o.x, -o.y ) * 2.0, sc ).ba ) );
          float u3 = step( 0.002, ccUnpack16( ccInkTap( uv + vec2( -o.x,  o.y ) * 2.0, sc ).ba ) );
          float k = solid + u0 + u1 + u2 + u3;
          float centred = clamp( 1.0 - abs( k - 4.0 ) * 0.25, 0.0, 1.0 );
          line = centred * uInkParams.x * uInkEdge.x;
        }

        outputColor.rgb = mix( inputColor.rgb, inputColor.rgb * uInkTint, clamp( line, 0.0, 1.0 ) );
      }
      `,
      {
        blendFunction: BlendFunction.SRC,
        uniforms: new Map<string, Uniform>([
          ['uGeoBuffer', new Uniform(null)],
          ['uGeoTexel', new Uniform(new Vector2(1 / 1024, 1 / 1024))],
          ['uInkTint', new Uniform(new Color(0.14, 0.13, 0.12))],
          ['uInkParams', new Uniform(new Vector4(0.55, 0.16, 0.004, 1))],
          ['uInkEdge', new Uniform(new Vector3(1, 1.2, 1 / 24))],
        ]),
      }
    );
  }

  /**
   * `pixelRatio` converts the CSS-pixel pen width into geo-buffer texels. The
   * geo buffer is sized from the drawing buffer, so without it a retina panel
   * draws a half-width line from the same setting.
   */
  apply(s: InkSettings, pixelRatio: number): void {
    const p = this.uniforms.get('uInkParams')!.value as Vector4;
    p.set(
      s.enabled ? Math.max(0, s.strength) : 0,
      Math.max(0.001, s.normalThreshold),
      Math.max(0.00002, s.depthThreshold),
      Math.max(0.25, s.thicknessPx * Math.max(0.5, pixelRatio))
    );
    const e = this.uniforms.get('uInkEdge')!.value as Vector3;
    e.x = Math.max(0, s.silhouette);
    e.y = Math.max(0, s.interiorGain);
    (this.uniforms.get('uInkTint')!.value as Color).copy(srgb(s.color));
  }

  /**
   * The depth window is refitted to the camera every frame, so the world-unit
   * seam threshold has to be re-normalised every frame too. Scalar write, no
   * allocation.
   */
  setDepthSpan(span: number): void {
    const e = this.uniforms.get('uInkEdge')!.value as Vector3;
    e.z = 1 / Math.max(1e-3, span);
  }

  bind(texture: WebGLRenderTarget['texture'], w: number, h: number): void {
    this.uniforms.get('uGeoBuffer')!.value = texture;
    (this.uniforms.get('uGeoTexel')!.value as Vector2).set(1 / Math.max(1, w), 1 / Math.max(1, h));
  }
}

/** Split-tone + saturation + a whisper of contrast, all in linear space. */
class ColorGradeEffect extends Effect {
  constructor() {
    super(
      'ColorGradeEffect',
      /* glsl */ `
      uniform vec3 uShadowTint;
      uniform vec3 uHighlightTint;
      uniform vec4 uGradeParams;  // x strength, y saturation, z contrast, w unused

      void mainImage( const in vec4 inputColor, const in vec2 uv, out vec4 outputColor ) {
        vec3 c = inputColor.rgb;
        if ( uGradeParams.x > 0.0 ) {
          float l = luminance( c );
          vec3 tint = mix( uShadowTint, uHighlightTint, smoothstep( 0.04, 0.7, l ) );
          c = mix( c, c * tint, uGradeParams.x );

          // Saturation about the luminance axis.
          float l2 = luminance( c );
          c = mix( vec3( l2 ), c, uGradeParams.y );

          // Contrast pivoted on linear middle grey.
          c = ( c - 0.18 ) * uGradeParams.z + 0.18;
          c = max( c, vec3( 0.0 ) );
        }
        outputColor = vec4( c, inputColor.a );
      }
      `,
      {
        blendFunction: BlendFunction.SRC,
        uniforms: new Map<string, Uniform>([
          ['uShadowTint', new Uniform(new Color(1, 1, 1))],
          ['uHighlightTint', new Uniform(new Color(1, 1, 1))],
          ['uGradeParams', new Uniform(new Vector4(0.5, 1.05, 1.035, 0))],
        ]),
      }
    );
  }

  apply(s: GradeSettings): void {
    const on = s.enabled ? Math.max(0, Math.min(1, s.strength)) : 0;
    (this.uniforms.get('uShadowTint')!.value as Color).setRGB(
      s.shadowTint[0],
      s.shadowTint[1],
      s.shadowTint[2]
    );
    (this.uniforms.get('uHighlightTint')!.value as Color).setRGB(
      s.highlightTint[0],
      s.highlightTint[1],
      s.highlightTint[2]
    );
    (this.uniforms.get('uGradeParams')!.value as Vector4).set(on, s.saturation, s.contrast, 0);
  }
}

/** Elliptical vignette tinted toward the shell green rather than black. */
class InkVignetteEffect extends Effect {
  constructor() {
    super(
      'InkVignetteEffect',
      /* glsl */ `
      uniform vec3 uVigTint;
      uniform vec3 uVigParams;  // x strength, y offset, z feather

      void mainImage( const in vec4 inputColor, const in vec2 uv, out vec4 outputColor ) {
        outputColor = inputColor;
        if ( uVigParams.x <= 0.0 ) return;
        vec2 p = ( uv - 0.5 ) * vec2( aspect, 1.0 ) * 2.0;
        float r = length( p ) * 0.66;
        float v = smoothstep( uVigParams.y, uVigParams.y + uVigParams.z, r );
        outputColor.rgb = mix( inputColor.rgb, inputColor.rgb * uVigTint, v * uVigParams.x );
      }
      `,
      {
        blendFunction: BlendFunction.SRC,
        uniforms: new Map<string, Uniform>([
          ['uVigTint', new Uniform(new Color(0.2, 0.3, 0.24))],
          ['uVigParams', new Uniform(new Vector3(0.3, 0.62, 0.66))],
        ]),
      }
    );
  }

  apply(s: VignetteSettings): void {
    (this.uniforms.get('uVigTint')!.value as Color).copy(srgb(s.color));
    (this.uniforms.get('uVigParams')!.value as Vector3).set(
      s.enabled ? Math.max(0, Math.min(1, s.strength)) : 0,
      Math.max(0.01, s.offset),
      Math.max(0.02, s.feather)
    );
  }
}

/**
 * Static, tiling paper grain from a baked canvas texture, locked to the WORLD.
 *
 * "Static" is still the whole point — animated grain reads as film noise, not
 * paper — but static *in screen space* is the opposite of paper the moment the
 * camera moves: the entire illustration slides underneath a fixed noise field,
 * which is the shower-door artifact, and it was a measurable part of the
 * texture flicker the user reported on 2026-08-07. Real paper grain travels with
 * the drawing.
 *
 * `uGrainOffset` is the camera's own position projected onto its right/up axes
 * and expressed as a fraction of the viewport, which under an orthographic
 * camera is exactly the screen-space translation of the world. Adding it to `uv`
 * pins the noise field to the world instead of to the glass. At rest the offset
 * is constant, so the grain is bit-for-bit still — which is the property the
 * original design wanted and screen-locking only appeared to give.
 */
class PaperGrainEffect extends Effect {
  constructor(texture: CanvasTexture) {
    super(
      'PaperGrainEffect',
      /* glsl */ `
      uniform sampler2D uPaper;
      uniform vec4 uGrainParams;  // x strength, y device px per texel, z fibre, w texture size
      uniform vec2 uGrainOffset;  // camera translation, in viewport fractions

      void mainImage( const in vec4 inputColor, const in vec2 uv, out vec4 outputColor ) {
        outputColor = inputColor;
        if ( uGrainParams.x <= 0.0 ) return;

        vec2 guv = ( uv + uGrainOffset ) * resolution / ( uGrainParams.y * uGrainParams.w );
        vec3 paper = texture2D( uPaper, guv ).rgb;

        // R is fine per-pixel tooth, G a slow fibre wander sampled 9x larger.
        float fine = paper.r - 0.5;
        float fibre = texture2D( uPaper, guv * 0.11 ).g - 0.5;
        float n = fine + fibre * uGrainParams.z;

        // Keep the ink solid: paper tooth shows in the paint, not in the line.
        float lum = luminance( inputColor.rgb );
        float mask = smoothstep( 0.012, 0.3, lum );

        outputColor.rgb = inputColor.rgb * ( 1.0 + n * uGrainParams.x * mask );
      }
      `,
      {
        blendFunction: BlendFunction.SRC,
        uniforms: new Map<string, Uniform>([
          ['uPaper', new Uniform(texture)],
          ['uGrainParams', new Uniform(new Vector4(0.05, 1.5, 0.45, 256))],
          ['uGrainOffset', new Uniform(new Vector2())],
        ]),
      }
    );
  }

  apply(s: GrainSettings, textureSize: number): void {
    (this.uniforms.get('uGrainParams')!.value as Vector4).set(
      s.enabled ? Math.max(0, s.strength) : 0,
      Math.max(0.25, s.scale),
      Math.max(0, s.fibre),
      textureSize
    );
  }

  /** Per-frame, allocation-free: writes into the existing uniform vector. */
  setOffset(x: number, y: number): void {
    (this.uniforms.get('uGrainOffset')!.value as Vector2).set(x, y);
  }
}

/* ------------------------------------------------------------------ *
 * Paper texture bake (no external assets — everything is generated)
 * ------------------------------------------------------------------ */

/** Deterministic PRNG so screenshots are byte-stable across runs. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Tileable bilinear value noise on a `cells`x`cells` lattice. */
function valueNoise(size: number, cells: number, rand: () => number): Float32Array {
  const lat = new Float32Array(cells * cells);
  for (let i = 0; i < lat.length; i++) lat[i] = rand();

  const out = new Float32Array(size * size);
  const scale = cells / size;
  for (let y = 0; y < size; y++) {
    const fy = y * scale;
    const y0 = Math.floor(fy) % cells;
    const y1 = (y0 + 1) % cells;
    const ty = fy - Math.floor(fy);
    const sy = ty * ty * (3 - 2 * ty);
    for (let x = 0; x < size; x++) {
      const fx = x * scale;
      const x0 = Math.floor(fx) % cells;
      const x1 = (x0 + 1) % cells;
      const tx = fx - Math.floor(fx);
      const sx = tx * tx * (3 - 2 * tx);
      const a = lat[y0 * cells + x0]!;
      const b = lat[y0 * cells + x1]!;
      const c = lat[y1 * cells + x0]!;
      const d = lat[y1 * cells + x1]!;
      out[y * size + x] = (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
    }
  }
  return out;
}

export const PAPER_TEXTURE_SIZE = 256;

/**
 * Bake the paper texture at runtime.
 *   R = fine tooth (per-texel white noise, slightly Gaussian-ised)
 *   G = slow fibre wander (two octaves of value noise)
 *   B = spare
 */
export function bakePaperGrain(size = PAPER_TEXTURE_SIZE): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('paper grain: 2d context unavailable');

  const rand = mulberry32(0x0cac01a);
  const fibreA = valueNoise(size, 8, rand);
  const fibreB = valueNoise(size, 23, rand);

  const img = ctx.createImageData(size, size);
  const d = img.data;
  for (let i = 0; i < size * size; i++) {
    // Average of three uniforms ~ a bell curve: far fewer harsh outlier specks
    // than raw white noise, which is what separates "tooth" from "static".
    const fine = (rand() + rand() + rand()) / 3;
    const fibre = fibreA[i]! * 0.65 + fibreB[i]! * 0.35;
    const o = i * 4;
    d[o] = Math.round(fine * 255);
    d[o + 1] = Math.round(fibre * 255);
    d[o + 2] = 128;
    d[o + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);

  const tex = new CanvasTexture(canvas);
  tex.name = 'PostFx.PaperGrain';
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.generateMipmaps = false;
  tex.colorSpace = NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/* ------------------------------------------------------------------ *
 * The stack
 * ------------------------------------------------------------------ */

export interface PostFxOptions {
  renderer: WebGLRenderer;
  scene: Scene;
  camera: OrthographicCamera;
  /**
   * Live reference to the point the camera looks at. Used to centre the depth
   * window; held by reference so the frame loop never allocates.
   */
  focus: Vector3;
  mood?: MoodId;
  /** MSAA samples for the offscreen buffer. Outlines are unusable without it. */
  multisampling?: number;
}

/** Bloom selection layer. Must not collide with `OUTLINE_LAYER` (1). */
export const GLOW_LAYER = 12;

const GLOW_SYNC_INTERVAL = 0.5;

export class PostFx {
  readonly composer: EffectComposer;
  readonly settings: PostFxSettings;

  private readonly renderer: WebGLRenderer;
  private readonly scene: Scene;
  private readonly camera: OrthographicCamera;
  private readonly focus: Vector3;

  private readonly geo: NormalDepthBuffer;
  private readonly paper: CanvasTexture;

  private readonly bloom: SelectiveBloomEffect;
  private readonly ink: InkLinesEffect;
  private readonly grade: ColorGradeEffect;
  private readonly vignette: InkVignetteEffect;
  private readonly grain: PaperGrainEffect;

  private readonly renderPass: RenderPass;
  private readonly bloomPass: EffectPass;
  private readonly lookPass: EffectPass;

  private readonly _size = new Vector2();
  /** Scratch for the world-locked grain offset. Reused; never allocated per frame. */
  private readonly _camRight = new Vector3();
  private readonly _camUp = new Vector3();
  private glowTimer = GLOW_SYNC_INTERVAL;
  private mood: MoodId;
  private failed = false;
  /** Device pixels per CSS pixel, so the pen keeps its physical width. */
  private pixelRatio = 1;

  /** Master switch. When false, `render` falls back to a direct scene render. */
  enabled = true;

  constructor(opts: PostFxOptions) {
    this.renderer = opts.renderer;
    this.scene = opts.scene;
    this.camera = opts.camera;
    this.focus = opts.focus;
    this.mood = opts.mood ?? 'hub';
    this.settings = structuredClonePreset(POSTFX_PRESETS[this.mood]);

    const maxSamples = this.renderer.capabilities.maxSamples ?? 0;
    const samples = Math.min(opts.multisampling ?? 4, maxSamples);

    this.composer = new EffectComposer(this.renderer, {
      // HDR buffer so emissive materials can exceed 1.0 and bloom meaningfully.
      frameBufferType: HalfFloatType,
      multisampling: samples,
      stencilBuffer: false,
      depthBuffer: true,
    });

    this.geo = new NormalDepthBuffer();
    this.paper = bakePaperGrain();

    this.bloom = new SelectiveBloomEffect(this.scene, this.camera, {
      blendFunction: BlendFunction.SCREEN,
      mipmapBlur: true,
      intensity: this.settings.bloom.intensity,
      radius: this.settings.bloom.radius,
      levels: this.settings.bloom.levels,
      luminanceThreshold: this.settings.bloom.luminanceThreshold,
      luminanceSmoothing: this.settings.bloom.luminanceSmoothing,
    });
    this.bloom.selection.layer = GLOW_LAYER;
    this.bloom.ignoreBackground = true;

    this.pixelRatio = this.renderer.getPixelRatio() || 1;

    this.ink = new InkLinesEffect();
    this.grade = new ColorGradeEffect();
    this.vignette = new InkVignetteEffect();
    this.grain = new PaperGrainEffect(this.paper);

    this.renderPass = new RenderPass(this.scene, this.camera);
    // Bloom is a convolution effect and must own its pass.
    this.bloomPass = new EffectPass(this.camera, this.bloom);
    // The rest merge into one fullscreen shader. Order is the paint order:
    // ink over the render, grade over the ink, vignette, then paper on top.
    this.lookPass = new EffectPass(this.camera, this.ink, this.grade, this.vignette, this.grain);

    this.composer.addPass(this.renderPass);
    this.composer.addPass(this.bloomPass);
    this.composer.addPass(this.lookPass);

    this.applySettings();
    this.syncGlowSelection();
  }

  get currentMood(): MoodId {
    return this.mood;
  }

  /** True once a render threw; the engine should stop calling us. */
  get isFailed(): boolean {
    return this.failed;
  }

  applyPreset(mood: MoodId): void {
    this.mood = mood;
    // Clone first: presets are shared module state and must never be aliased
    // into a live settings object that scenes are free to mutate.
    assignSettings(this.settings, structuredClonePreset(POSTFX_PRESETS[mood]));
    this.applySettings();
  }

  /** Patch any subset of the settings tree, then re-upload uniforms. */
  configure(patch: DeepPartial<PostFxSettings>): void {
    assignSettings(this.settings, patch);
    this.applySettings();
  }

  /** Individual on/off, exactly as the brief requires. */
  setEffectEnabled(name: EffectName, on: boolean): void {
    this.settings[name].enabled = on;
    this.applySettings();
  }

  isEffectEnabled(name: EffectName): boolean {
    return this.settings[name].enabled;
  }

  /** Push the whole settings tree into GPU uniforms. Cheap; call freely. */
  applySettings(): void {
    const s = this.settings;

    this.ink.apply(s.ink, this.pixelRatio);
    this.grade.apply(s.grade);
    this.vignette.apply(s.vignette);
    this.grain.apply(s.grain, PAPER_TEXTURE_SIZE);

    this.bloom.intensity = s.bloom.intensity;
    this.bloom.mipmapBlurPass.radius = s.bloom.radius;
    this.bloom.luminanceMaterial.threshold = s.bloom.luminanceThreshold;
    this.bloom.luminanceMaterial.smoothing = s.bloom.luminanceSmoothing;

    // NOTE: the look pass must stay `enabled` even with every effect switched
    // off. It is the composer's last pass, which is the one `autoRenderToScreen`
    // flagged to blit to the canvas — disabling it leaves the canvas holding
    // whatever was there before, i.e. a frozen or garbage frame. Each effect is
    // strength-gated inside the shader instead, so "all off" costs one
    // fullscreen copy and nothing more.
  }

  /* ---------------- glow selection ---------------- */

  /** Tag an object (and its subtree) as a bloom source. */
  addGlow(object: Object3D): void {
    object.traverse((o) => {
      if (o.userData.isOutline) return;
      this.bloom.selection.add(o);
    });
  }

  removeGlow(object: Object3D): void {
    object.traverse((o) => this.bloom.selection.delete(o));
  }

  /**
   * Rebuild the bloom selection from the scene graph.
   *
   * Convention, so scenes need no knowledge of this file: an object glows if it
   * sets `userData.glow = true`, or if its material has a non-black emissive.
   * `userData.glow = false` opts out explicitly.
   */
  syncGlowSelection(): void {
    const sel = this.bloom.selection;
    sel.clear();
    this.scene.traverse((o) => {
      if (o.userData.isOutline) return;
      if (o.userData.glow === false) return;
      if (o.userData.glow === true || hasEmissive(o)) sel.add(o);
    });
  }

  get glowCount(): number {
    return this.bloom.selection.size;
  }

  /* ---------------- frame ---------------- */

  /** `w`/`h` are CSS pixels; the composer scales by the renderer pixel ratio. */
  setSize(w: number, h: number): void {
    this.composer.setSize(Math.max(1, w), Math.max(1, h), false);
    this.renderer.getDrawingBufferSize(this._size);
    this.geo.setSize(this._size.x, this._size.y);
    this.ink.bind(this.geo.target.texture, this._size.x, this._size.y);
    // Derived from the buffers rather than read off `window`, so an offscreen or
    // downscaled canvas still resolves the pen to the right number of texels.
    const ratio = this._size.y / Math.max(1, h);
    if (Number.isFinite(ratio) && ratio > 0 && Math.abs(ratio - this.pixelRatio) > 1e-3) {
      this.pixelRatio = ratio;
      this.ink.apply(this.settings.ink, this.pixelRatio);
    }
  }

  /**
   * Pin the paper grain to the world rather than to the screen.
   *
   * Under an orthographic camera, projection is a rigid map, so the screen
   * displacement of the whole world is just the camera's position measured along
   * its own right/up axes, divided by the world span the viewport covers. Feed
   * that back into the grain's lookup and the noise field stops sliding under
   * the illustration when the camera pans. Bails out when grain is off, so a
   * mood with `strength: 0` pays nothing.
   */
  private updateGrainOffset(): void {
    const g = this.settings.grain;
    if (!g.enabled || g.strength <= 0) return;

    const cam = this.camera;
    const zoom = cam.zoom || 1;
    const spanX = (cam.right - cam.left) / zoom;
    const spanY = (cam.top - cam.bottom) / zoom;
    if (!(Math.abs(spanX) > 1e-6) || !(Math.abs(spanY) > 1e-6)) return;

    this._camRight.setFromMatrixColumn(cam.matrixWorld, 0).normalize();
    this._camUp.setFromMatrixColumn(cam.matrixWorld, 1).normalize();

    this.grain.setOffset(
      this.focus.dot(this._camRight) / spanX,
      this.focus.dot(this._camUp) / spanY
    );
  }

  /**
   * Render one frame. Returns false if the stack failed and the caller should
   * fall back to a plain `renderer.render`.
   */
  render(dt: number): boolean {
    if (!this.enabled || this.failed) return false;

    try {
      // Bloom is genuinely free when nothing in the scene glows.
      this.bloomPass.enabled = this.settings.bloom.enabled && this.bloom.selection.size > 0;

      this.updateGrainOffset();

      const wantsInk = this.settings.ink.enabled && this.settings.ink.strength > 0;
      if (wantsInk) {
        // The window refits to the camera every frame, so the world-unit seam
        // threshold has to be re-normalised against it every frame.
        this.ink.setDepthSpan(this.geo.setDepthWindow(this.camera, this.focus));
        this.geo.render(this.renderer, this.scene, this.camera);
      }

      this.glowTimer += dt;
      if (this.glowTimer >= GLOW_SYNC_INTERVAL) {
        this.glowTimer = 0;
        this.syncGlowSelection();
      }

      this.composer.render(dt);
      return true;
    } catch (err) {
      console.error('[postfx] render failed, falling back to direct render', err);
      this.failed = true;
      return false;
    }
  }

  dispose(): void {
    this.composer.dispose();
    this.geo.dispose();
    this.paper.dispose();
    this.bloom.selection.clear();
  }
}

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? Partial<T[K]> : T[K] };

function hasEmissive(o: Object3D): boolean {
  const mat = (o as Mesh).material;
  if (!mat) return false;
  const list = Array.isArray(mat) ? mat : [mat];
  for (const m of list) {
    const em = (m as { emissive?: Color; emissiveIntensity?: number }).emissive;
    const ei = (m as { emissiveIntensity?: number }).emissiveIntensity ?? 1;
    if (em && ei > 0 && em.r + em.g + em.b > 0.02) return true;
  }
  return false;
}

/** Structural clone that keeps the tuple types intact. */
function structuredClonePreset(src: PostFxSettings): PostFxSettings {
  return {
    bloom: { ...src.bloom },
    ink: { ...src.ink },
    grade: {
      ...src.grade,
      shadowTint: [...src.grade.shadowTint] as [number, number, number],
      highlightTint: [...src.grade.highlightTint] as [number, number, number],
    },
    vignette: { ...src.vignette },
    grain: { ...src.grain },
  };
}

function assignSettings(dst: PostFxSettings, patch: DeepPartial<PostFxSettings>): void {
  for (const key of Object.keys(patch) as EffectName[]) {
    const group = patch[key];
    if (!group) continue;
    Object.assign(dst[key], group);
  }
}

/**
 * Build the stack, or return `null` if anything at all goes wrong.
 *
 * Post-processing is a garnish. A browser that cannot give us a float render
 * target, or a driver that chokes on the composer, must still get a playable
 * game — so every failure path here degrades to the plain forward renderer
 * rather than throwing into the engine's constructor.
 */
export function createPostFx(opts: PostFxOptions): PostFx | null {
  try {
    if (!opts.renderer.capabilities.isWebGL2) return null;
    return new PostFx(opts);
  } catch (err) {
    console.error('[postfx] init failed, continuing without post-processing', err);
    return null;
  }
}
